import { createContext, useContext, useState, useMemo, useCallback } from 'react';

const ReportContext = createContext(null);

// Report status types
export const REPORT_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  ARCHIVED: 'archived',
};

// Helper function to get the report week boundaries
// Week starts: Tuesday 12:00 PM
// Week ends: Next Tuesday 11:59:59 AM
export const getReportWeekBoundaries = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday, 2 = Tuesday
  const hour = d.getHours();

  // Calculate days since last Tuesday 12:00 PM
  let daysSinceTuesday = day - 2; // Tuesday is day 2
  if (daysSinceTuesday < 0) daysSinceTuesday += 7;

  // If it's Tuesday but before noon, we're still in the previous week
  if (day === 2 && hour < 12) {
    daysSinceTuesday = 7;
  }

  // Calculate week start (Tuesday 12:00 PM)
  const weekStart = new Date(d);
  weekStart.setDate(d.getDate() - daysSinceTuesday);
  weekStart.setHours(12, 0, 0, 0);

  // Calculate week end (Next Tuesday 11:59:59 AM)
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  weekEnd.setHours(11, 59, 59, 999);

  return { weekStart, weekEnd };
};

// Get report week label (e.g., "01-21-2026 to 01-28-2026")
export const getReportWeekLabel = (date = new Date()) => {
  const { weekStart, weekEnd } = getReportWeekBoundaries(date);

  const formatDate = (d) => {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  return `${formatDate(weekStart)} to ${formatDate(weekEnd)}`;
};

// Check if current time is before the deadline (Tuesday 12:00 PM)
export const isBeforeDeadline = (date = new Date()) => {
  const { weekEnd } = getReportWeekBoundaries(date);
  return date < weekEnd;
};

// Get time remaining until deadline
export const getTimeUntilDeadline = (date = new Date()) => {
  const { weekEnd } = getReportWeekBoundaries(date);
  const diff = weekEnd - date;

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, passed: true };

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes, passed: false };
};

// Helper to generate past week dates
const getPastWeekDates = (weeksAgo) => {
  const now = new Date();
  const pastDate = new Date(now);
  pastDate.setDate(now.getDate() - weeksAgo * 7);

  const day = pastDate.getDay();
  let daysSinceTuesday = day - 2;
  if (daysSinceTuesday < 0) daysSinceTuesday += 7;

  const weekStart = new Date(pastDate);
  weekStart.setDate(pastDate.getDate() - daysSinceTuesday);
  weekStart.setHours(12, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 7);
  weekEnd.setHours(11, 59, 59, 999);

  return {
    weekStart: weekStart.toISOString().split('T')[0],
    weekEnd: weekEnd.toISOString().split('T')[0],
  };
};

// Initial reports with dummy historical data
const initialReports = [
  // 1 week ago - Published
  {
    id: 'r1001',
    ...getPastWeekDates(1),
    status: REPORT_STATUS.PUBLISHED,
    publishedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    entries: [
      { id: 'e1001', projectId: '1', projectName: 'Website Redesign', taskId: 't1', taskName: 'Homepage Design', status: 'in_progress', progress: 75, notes: 'Completed hero section, working on footer', updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'John Doe' },
      { id: 'e1002', projectId: '1', projectName: 'Website Redesign', taskId: 't2', taskName: 'Backend API', status: 'in_progress', progress: 50, notes: 'API endpoints 50% complete', updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Sarah Johnson' },
      { id: 'e1003', projectId: '2', projectName: 'Mobile App Development', taskId: 't4', taskName: 'User Authentication', status: 'completed', progress: 100, notes: 'OAuth integration complete', updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Mike Wilson' },
    ],
    auditLog: [
      { action: 'created', timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
      { action: 'entry_added', timestamp: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(), user: 'John Doe' },
      { action: 'entry_updated', timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), user: 'Sarah Johnson' },
      { action: 'published', timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
    ],
  },
  // 2 weeks ago - Published
  {
    id: 'r1002',
    ...getPastWeekDates(2),
    status: REPORT_STATUS.PUBLISHED,
    publishedAt: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    entries: [
      { id: 'e2001', projectId: '1', projectName: 'Website Redesign', taskId: 't1', taskName: 'Homepage Design', status: 'in_progress', progress: 50, notes: 'Wireframes approved, starting development', updatedAt: new Date(Date.now() - 9 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'John Doe' },
      { id: 'e2002', projectId: '1', projectName: 'Website Redesign', taskId: 't2', taskName: 'Backend API', status: 'in_progress', progress: 25, notes: 'Database schema designed', updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Sarah Johnson' },
      { id: 'e2003', projectId: '2', projectName: 'Mobile App Development', taskId: 't4', taskName: 'User Authentication', status: 'in_progress', progress: 60, notes: 'Working on OAuth flow', updatedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Mike Wilson' },
      { id: 'e2004', projectId: '3', projectName: 'Cloud Migration', taskId: 't7', taskName: 'Infrastructure Setup', status: 'in_progress', progress: 40, notes: 'AWS resources provisioned', updatedAt: new Date(Date.now() - 11 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Emily Davis' },
    ],
    auditLog: [
      { action: 'created', timestamp: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
      { action: 'entry_added', timestamp: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000).toISOString(), user: 'John Doe' },
      { action: 'published', timestamp: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
    ],
  },
  // 3 weeks ago - Published
  {
    id: 'r1003',
    ...getPastWeekDates(3),
    status: REPORT_STATUS.PUBLISHED,
    publishedAt: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(),
    entries: [
      { id: 'e3001', projectId: '1', projectName: 'Website Redesign', taskId: 't1', taskName: 'Homepage Design', status: 'in_progress', progress: 25, notes: 'Requirement gathering complete', updatedAt: new Date(Date.now() - 16 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'John Doe' },
      { id: 'e3002', projectId: '2', projectName: 'Mobile App Development', taskId: 't4', taskName: 'User Authentication', status: 'in_progress', progress: 30, notes: 'Started login screen UI', updatedAt: new Date(Date.now() - 17 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Mike Wilson' },
    ],
    auditLog: [
      { action: 'created', timestamp: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
      { action: 'entry_added', timestamp: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(), user: 'John Doe' },
      { action: 'published', timestamp: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
    ],
  },
  // 4 weeks ago - Published
  {
    id: 'r1004',
    ...getPastWeekDates(4),
    status: REPORT_STATUS.PUBLISHED,
    publishedAt: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(),
    entries: [
      { id: 'e4001', projectId: '1', projectName: 'Website Redesign', taskId: 't1', taskName: 'Homepage Design', status: 'not_started', progress: 0, notes: 'Project kickoff meeting scheduled', updatedAt: new Date(Date.now() - 23 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'John Doe' },
      { id: 'e4002', projectId: '3', projectName: 'Cloud Migration', taskId: 't7', taskName: 'Infrastructure Setup', status: 'in_progress', progress: 15, notes: 'Initial assessment complete', updatedAt: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Emily Davis' },
      { id: 'e4003', projectId: '3', projectName: 'Cloud Migration', taskId: 't8', taskName: 'Data Migration', status: 'not_started', progress: 0, notes: 'Planning phase', updatedAt: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Emily Davis' },
    ],
    auditLog: [
      { action: 'created', timestamp: new Date(Date.now() - 28 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
      { action: 'entry_added', timestamp: new Date(Date.now() - 27 * 24 * 60 * 60 * 1000).toISOString(), user: 'John Doe' },
      { action: 'entry_updated', timestamp: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(), user: 'Emily Davis' },
      { action: 'published', timestamp: new Date(Date.now() - 22 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
    ],
  },
  // 5 weeks ago - Archived
  {
    id: 'r1005',
    ...getPastWeekDates(5),
    status: REPORT_STATUS.ARCHIVED,
    publishedAt: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString(),
    entries: [
      { id: 'e5001', projectId: '4', projectName: 'Security Audit', taskId: 't10', taskName: 'Vulnerability Assessment', status: 'completed', progress: 100, notes: 'All critical vulnerabilities patched', updatedAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Robert Brown' },
      { id: 'e5002', projectId: '4', projectName: 'Security Audit', taskId: 't11', taskName: 'Penetration Testing', status: 'completed', progress: 100, notes: 'Pen test complete, report delivered', updatedAt: new Date(Date.now() - 31 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Robert Brown' },
    ],
    auditLog: [
      { action: 'created', timestamp: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
      { action: 'entry_added', timestamp: new Date(Date.now() - 34 * 24 * 60 * 60 * 1000).toISOString(), user: 'Robert Brown' },
      { action: 'published', timestamp: new Date(Date.now() - 29 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
    ],
  },
  // 6 weeks ago - Archived
  {
    id: 'r1006',
    ...getPastWeekDates(6),
    status: REPORT_STATUS.ARCHIVED,
    publishedAt: new Date(Date.now() - 36 * 24 * 60 * 60 * 1000).toISOString(),
    entries: [
      { id: 'e6001', projectId: '5', projectName: 'Documentation Update', taskId: 't12', taskName: 'API Documentation', status: 'completed', progress: 100, notes: 'All endpoints documented', updatedAt: new Date(Date.now() - 37 * 24 * 60 * 60 * 1000).toISOString(), updatedBy: 'Lisa Chen' },
    ],
    auditLog: [
      { action: 'created', timestamp: new Date(Date.now() - 42 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
      { action: 'entry_added', timestamp: new Date(Date.now() - 41 * 24 * 60 * 60 * 1000).toISOString(), user: 'Lisa Chen' },
      { action: 'published', timestamp: new Date(Date.now() - 36 * 24 * 60 * 60 * 1000).toISOString(), user: 'System' },
    ],
  },
];

export const ReportProvider = ({ children }) => {
  const [reports, setReports] = useState(initialReports);

  // Auto-generate or update current week's report from project data
  const generateReportFromProjects = useCallback((activeTasks, userName = 'System') => {
    const { weekStart, weekEnd } = getReportWeekBoundaries();
    const weekStartStr = weekStart.toISOString().split('T')[0];
    const weekEndStr = weekEnd.toISOString().split('T')[0];

    setReports((prev) => {
      // Check if report exists for current week
      const existingIndex = prev.findIndex((r) => r.weekStart === weekStartStr);

      // Generate entries from active tasks
      const entries = activeTasks.map((task, idx) => ({
        id: `e${Date.now()}_${idx}`,
        projectId: task.projectId,
        projectName: task.projectName,
        taskId: task.taskId,
        taskName: task.taskName,
        status: task.status,
        progress: task.progress,
        notes: '',
        updatedAt: new Date().toISOString(),
        updatedBy: userName,
      }));

      if (existingIndex >= 0) {
        // Update existing report - merge entries
        const existingReport = prev[existingIndex];
        const updatedEntries = [...existingReport.entries];

        entries.forEach((newEntry) => {
          const existingEntryIndex = updatedEntries.findIndex(
            (e) => e.projectId === newEntry.projectId && e.taskId === newEntry.taskId
          );
          if (existingEntryIndex >= 0) {
            // Update existing entry with new progress/status but keep notes
            updatedEntries[existingEntryIndex] = {
              ...updatedEntries[existingEntryIndex],
              status: newEntry.status,
              progress: newEntry.progress,
              updatedAt: new Date().toISOString(),
            };
          } else {
            // Add new entry
            updatedEntries.push(newEntry);
          }
        });

        const updatedReport = {
          ...existingReport,
          entries: updatedEntries,
          auditLog: [
            ...existingReport.auditLog,
            {
              action: 'entry_updated',
              timestamp: new Date().toISOString(),
              user: 'System',
              details: 'Auto-synced from project data',
            },
          ],
        };

        const newReports = [...prev];
        newReports[existingIndex] = updatedReport;
        return newReports;
      } else {
        // Create new report
        const newReport = {
          id: `r${Date.now()}`,
          weekStart: weekStartStr,
          weekEnd: weekEndStr,
          status: REPORT_STATUS.DRAFT,
          publishedAt: null,
          entries,
          auditLog: [
            { action: 'created', timestamp: new Date().toISOString(), user: 'System' },
            { action: 'entry_added', timestamp: new Date().toISOString(), user: 'System', details: 'Auto-generated from project data' },
          ],
        };
        return [...prev, newReport];
      }
    });
  }, []);

  // Get current week's report
  const getCurrentWeekReport = useCallback(() => {
    const { weekStart } = getReportWeekBoundaries();
    const weekStartStr = weekStart.toISOString().split('T')[0];

    return reports.find((r) => r.weekStart === weekStartStr) || null;
  }, [reports]);

  // Get report by ID
  const getReportById = useCallback((reportId) => {
    return reports.find((r) => r.id === reportId) || null;
  }, [reports]);

  // Get report by week
  const getReportByWeek = useCallback((weekStartDate) => {
    const weekStartStr = new Date(weekStartDate).toISOString().split('T')[0];
    return reports.find((r) => r.weekStart === weekStartStr) || null;
  }, [reports]);

  // Add or update report entry
  const addReportEntry = useCallback((reportId, entry) => {
    setReports((prev) =>
      prev.map((report) => {
        if (report.id !== reportId) return report;

        const existingIndex = report.entries.findIndex(
          (e) => e.projectId === entry.projectId && e.taskId === entry.taskId
        );

        let newEntries;
        if (existingIndex >= 0) {
          newEntries = [...report.entries];
          newEntries[existingIndex] = {
            ...newEntries[existingIndex],
            ...entry,
            updatedAt: new Date().toISOString(),
          };
        } else {
          newEntries = [
            ...report.entries,
            {
              id: `e${Date.now()}`,
              ...entry,
              updatedAt: new Date().toISOString(),
            },
          ];
        }

        return {
          ...report,
          entries: newEntries,
          auditLog: [
            ...report.auditLog,
            {
              action: existingIndex >= 0 ? 'entry_updated' : 'entry_added',
              timestamp: new Date().toISOString(),
              user: entry.updatedBy,
            },
          ],
        };
      })
    );
  }, []);

  // Create new report for current week
  const createWeekReport = useCallback(() => {
    const { weekStart, weekEnd } = getReportWeekBoundaries();
    const weekStartStr = weekStart.toISOString().split('T')[0];
    const weekEndStr = weekEnd.toISOString().split('T')[0];

    const existing = reports.find((r) => r.weekStart === weekStartStr);
    if (existing) return existing;

    const newReport = {
      id: `r${Date.now()}`,
      weekStart: weekStartStr,
      weekEnd: weekEndStr,
      status: REPORT_STATUS.DRAFT,
      publishedAt: null,
      entries: [],
      auditLog: [
        { action: 'created', timestamp: new Date().toISOString(), user: 'System' },
      ],
    };

    setReports((prev) => [...prev, newReport]);
    return newReport;
  }, [reports]);

  // Update report entry (for corrections after publish)
  const updateReportEntry = useCallback((reportId, entryId, updates, userId) => {
    setReports((prev) =>
      prev.map((report) => {
        if (report.id !== reportId) return report;

        return {
          ...report,
          entries: report.entries.map((entry) =>
            entry.id === entryId
              ? { ...entry, ...updates, updatedAt: new Date().toISOString() }
              : entry
          ),
          auditLog: [
            ...report.auditLog,
            {
              action: 'entry_corrected',
              timestamp: new Date().toISOString(),
              user: userId,
              details: `Entry ${entryId} modified`,
            },
          ],
        };
      })
    );
  }, []);

  // Get all reports sorted by date (newest first)
  const getAllReports = useCallback(() => {
    return [...reports].sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart));
  }, [reports]);

  // Publish current week's report
  const publishReport = useCallback((reportId, userId = 'System') => {
    setReports((prev) =>
      prev.map((report) => {
        if (report.id !== reportId) return report;

        return {
          ...report,
          status: REPORT_STATUS.PUBLISHED,
          publishedAt: new Date().toISOString(),
          auditLog: [
            ...report.auditLog,
            {
              action: 'published',
              timestamp: new Date().toISOString(),
              user: userId,
            },
          ],
        };
      })
    );
  }, []);

  // Delete entry from report
  const deleteReportEntry = useCallback((reportId, entryId, userId) => {
    setReports((prev) =>
      prev.map((report) => {
        if (report.id !== reportId) return report;

        return {
          ...report,
          entries: report.entries.filter((e) => e.id !== entryId),
          auditLog: [
            ...report.auditLog,
            {
              action: 'entry_deleted',
              timestamp: new Date().toISOString(),
              user: userId,
              details: `Entry ${entryId} deleted`,
            },
          ],
        };
      })
    );
  }, []);

  const value = useMemo(
    () => ({
      reports,
      getCurrentWeekReport,
      getReportById,
      getReportByWeek,
      addReportEntry,
      createWeekReport,
      updateReportEntry,
      getAllReports,
      publishReport,
      deleteReportEntry,
      generateReportFromProjects,
      getReportWeekBoundaries,
      getReportWeekLabel,
      isBeforeDeadline,
      getTimeUntilDeadline,
      REPORT_STATUS,
    }),
    [
      reports,
      getCurrentWeekReport,
      getReportById,
      getReportByWeek,
      addReportEntry,
      createWeekReport,
      updateReportEntry,
      getAllReports,
      publishReport,
      deleteReportEntry,
      generateReportFromProjects,
    ]
  );

  return (
    <ReportContext.Provider value={value}>
      {children}
    </ReportContext.Provider>
  );
};

export const useReport = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReport must be used within a ReportProvider');
  }
  return context;
};

export default ReportContext;
